import _ from "lodash";
import {gettextCatalog} from "angular-gettext";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {RessursFeilhåndtering} from "../tjenester/RessursFeilhåndtering";
import {Debugger} from "debug";
import {resource} from "angular";

const debug: Debugger = require('debug')('BasisRessurs');

/**
 * Basisklasse for angular ressursinstanser.
 *
 * Merk at ressursmetodene er deklarert til å rerurnere T fremfor IResource da
 * returen i praksis kan håndteres som T.
 *
 * Merk at api-kall er angitt med ikke-absolutt url (/api/..) - konvertering til miljøspesifikk
 * absolutt url skje i $http interceptor definert i i.APIKallUrlhaandtering
 *
 * Se lederplan-tjenester/src/main/java/no/systemfabrikken/lederplan/infrastruktur/sikkerhet/håndtering_av_jwt.md
 * for informasjon om requestflyten ifbm jwt håndtering
 */
export class BasisRessurs<T> {
    
    public ressurs: ng.resource.IResourceClass<ng.resource.IResource<T>>;

    constructor(private ressursnavn: string,
                private $resource: ng.resource.IResourceService,
                public ressursFeilhåndtering: RessursFeilhåndtering,
                public gettextCatalog: gettextCatalog,
                tilleggsOpperasjoner = {}) {
        this.ressurs = $resource(
            "/api/" + ressursnavn + "/:id",
            {id: '@id'},
            _.defaults(
                tilleggsOpperasjoner,
                {'opprett': {method: 'PUT', params: {id: ""}} /*om params utelates blir url /<ressursnavn>/id..*/}
            )
        );
    }

    //offentlige metoder

    hent(id: string, vedOk: (entitet: T) => void = _.noop, vedFeil: (feil: resource.IResourceResponse) => boolean = () => false): T {
        debug(`${this.ressursnavn}.hent: %s`, id);
        return <T><any>this.ressurs.get(
            {id: id},
            vedOk,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste {{ressursnavn}} med id {{id}}', {ressursnavn: this.ressursnavn, id: id}), vedFeil)
        );
    }

    opprett(spesifikasjon, vedOk: (entitet: T) => void = _.noop, vedFeil: (feil: resource.IResourceResponse) => boolean = () => false): T {
        debug(`${this.ressursnavn}.opprett: %j`, spesifikasjon);
        return <T><any>this.ressurs["opprett"](
            spesifikasjon,
            vedOk,
            this.håndterFeil(()=> this.gettextCatalog.getString('Kunne ikke opprette {{ressursnavn}}',{ressursnavn:this.ressursnavn}), vedFeil)
        );
    }

    oppdater(spesifikasjon, vedOk: (entitet: T) => void = _.noop, vedFeil: (feil: resource.IResourceResponse) => boolean = () => false): T {
        debug(`${this.ressursnavn}.oppdater: %j`, spesifikasjon);
        return <T><any>this.ressurs.save(
            spesifikasjon,
            vedOk,
            this.håndterFeil(()=> this.gettextCatalog.getString('Kunne ikke oppdatere {{ressursnavn}}', {ressursnavn:this.ressursnavn}), vedFeil)
        );
    }

    slett(id: string, vedOk: () => void = _.noop, vedFeil: (feil: resource.IResourceResponse) => boolean = () => false): l.OkRespons {
        debug(`${this.ressursnavn}.slett: %s`, id);
        return this.ressurs.delete(
            {id: id},
            vedOk,
            this.håndterFeil(()=> this.gettextCatalog.getString('Kunne ikke slette {{ressursnavn}}', {ressursnavn:this.ressursnavn}), vedFeil)
        );
    }

    håndterFeil(melding: () => string,
                eksternHåndtering: (feil: resource.IResourceResponse) => boolean = () => false): (feil: resource.IResourceResponse) => void {
        return this.ressursFeilhåndtering.håndterRequestFeil(melding, null, eksternHåndtering);
    }

    public static erResolvedOgIkkeNull(enitet: l.EntitetModell<any>) {
        if (_.isNil(enitet)) return false;
        if (enitet.$resolved === false) return false;
        return true;
    }
}



/*
Kopiert fra LederplanExceptionMapperBinder
case møteIkkePublisert: return Statuses.from(460, "møte ikke publisert");
case protokollIkkePublisert: return Statuses.from(461, "protokoll ikke publisert");
case skjermet: return Statuses.from(462, "skjermet");
case privat: return Statuses.from(463, "privat");
case gruppetilgang:
case ikkeSpesifisertÅrsak:
default: return NOT_FOUND; // Vi bruker 404 istedenfor 403 (Forbidden) så apiet ikke avslører at entiteten finnes.
 */
export enum HttpStatus {
    møteIkkePublisert=460,
    protokollIkkePublisert=461,
    skjermet=462,
    privat= 463,
    forbudt=401,
    ikkeFunnet=404,
    gone=410
}
